import React from 'react';
import PropTypes from 'prop-types';
import { Table, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { convertISOToDateTimeFormat } from '../../../util/timezone';

export const FRAML_CANONICAL_VIEW = 'FRAML_CANONICAL_V1';

// FIN-13688: Search-by matches the visible Sender/Receiver columns, not the customer-relative
// Counterparty. Each value is the request param the BE filters on.
export const framlCanonicalSearchFields = [
  { value: 'transactionId', label: 'Transaction ID' },
  { value: 'partyId', label: 'Sender or Receiver ID' },
  { value: 'senderId', label: 'Sender ID' },
  { value: 'receiverId', label: 'Receiver ID' },
  { value: 'senderName', label: 'Sender Name' },
  { value: 'receiverName', label: 'Receiver Name' }
];
const display = (value) =>
  value === null || value === undefined || value === '' ? '-' : String(value);

// Keep the order and labels aligned with HardcodedColumnConfig.FramlUnified.
export const framlCanonicalColumns = [
  ['id', 'Transaction ID'],
  ['transactionDate', 'Transaction Time'],
  ['sender.key', 'Sender ID'],
  ['sender.name', 'Sender Name'],
  ['sender.account', 'Sender Account'],
  ['receiver.key', 'Receiver ID'],
  ['receiver.name', 'Receiver Name'],
  ['receiver.account', 'Receiver Account'],
  ['transactionCurrencyAmount', 'Sender Amount'],
  ['transactionCurrency', 'Sender Currency'],
  ['receiverTransactionAmount', 'Receiver Amount'],
  ['receiverTransactionCurrency', 'Receiver Currency']
].map(([key, title]) => ({
  key,
  title,
  dataIndex: key.split('.'),
  render: (value, row) => {
    if (key === 'transactionDate' && value)
      return convertISOToDateTimeFormat(value);
    // FIN-13000: an alert's focal transaction the client has not yet acknowledged.
    if (key === 'id' && row?.acked === 0) {
      return (
        <span>
          {display(value)} <Tag color="orange">Pending</Tag>
        </span>
      );
    }
    return display(value);
  }
}));

// Evidence is keyed by the business transaction: (id, date). Evidence items
// carry nothing finer, so this is the key to group and look evidence up by.
export const canonicalTransactionKey = (row) =>
  JSON.stringify([row.id, row.transactionDate ?? null]);

// The TABLE row key must be unique per physical row, or antd expands every row
// sharing it (FIN-13758): `id` can repeat across rows with the same date.
// transactionRecordId is the physical-row id; it is null on rows the FIN-12657
// backfill has not reached, so the two party keys break the tie there.
export const canonicalTransactionRowKey = (row) =>
  JSON.stringify([
    row.id,
    row.transactionDate ?? null,
    row.transactionRecordId ?? null,
    row.sender?.key ?? null,
    row.receiver?.key ?? null
  ]);

export const transactionEvidence = (row, evidence = []) =>
  (Array.isArray(evidence) ? evidence : []).filter(
    (item) =>
      item.transactionId === row.id &&
      item.transactionDate === row.transactionDate
  );

export const FramlTransactionVolumes = ({ volumes = [] }) => (
  <div
    aria-label="Transaction volume by currency"
    style={{ padding: '8px 16px' }}
  >
    <strong>Transaction volume (all matching transactions)</strong>
    {volumes.length === 0 ? (
      <div>No matching transactions</div>
    ) : (
      volumes.map((volume) => (
        <div key={JSON.stringify(volume.currency ?? null)}>
          {volume.currency || 'Unknown currency'}: {display(volume.amount)}
          {' · '}
          {volume.transactionCount} transactions
          {volume.unknownAmountCount > 0 &&
            ` · ${volume.unknownAmountCount} with unknown amount`}
        </div>
      ))
    )}
  </div>
);

FramlTransactionVolumes.propTypes = { volumes: PropTypes.array };

// FIN-13680: AML-TM alerts (TMIAD production 6, run instance 8) detect at typology level and
// carry no rule in any store, so their Rule cell is an explained dash, not a missing value.
export const TM_SOURCE_IDS = [6, 8];
export const TM_NO_RULE_TOOLTIP = 'AML-TM alerts are typology-based; no rule';
// FIN-13759: evidence is limited to the customer's latest live score snapshot, so older alerts
// on the same transaction are absent by design. Say so, or the table reads as incomplete.
export const TRIGGER_ASSOCIATIONS_SCOPE_TOOLTIP =
  "Only alerts in the customer's current FRAML score that triggered on " +
  'this transaction. Earlier alerts no longer contributing to the score ' +
  'are not listed.';
const isTmSource = (sourceId) => TM_SOURCE_IDS.includes(Number(sourceId));
const hasText = (value) =>
  value !== null && value !== undefined && String(value).trim() !== '';

// The expanded row already carries both participants, so the matched party's name needs no read.
export const matchedPartyLabel = (partyKey, row) => {
  if (!hasText(partyKey)) return '-';
  const party = [row?.sender, row?.receiver].find(
    (participant) => participant?.key === partyKey
  );
  return hasText(party?.name)
    ? `${party.name} (${partyKey})`
    : String(partyKey);
};

const uniqueBy = (values, keyOf = (value) => value) => {
  const seen = new Set();
  return values.filter((value) => {
    const key = keyOf(value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const joined = (values) => (values.length ? values.join(', ') : '-');

// FIN-13678: the BE returns one association per (alert, source, typology, rule, party), so a
// single alert with five typologies filled five rows and the pager counted those rows, not
// alerts. Collapse them to one row per alert BEFORE the table sees them, merging the
// typologies, rules and matched parties (de-duplicated, first-seen order). An association with
// no alertId has nothing to merge on and stays its own row.
export const groupEvidenceByAlert = (evidence = []) => {
  const groups = [];
  const byAlert = new Map();
  (Array.isArray(evidence) ? evidence : []).forEach((item) => {
    const alertId = hasText(item?.alertId) ? String(item.alertId) : null;
    let group = alertId !== null ? byAlert.get(alertId) : undefined;
    if (!group) {
      group = {
        groupKey:
          alertId !== null
            ? JSON.stringify(['alert', alertId])
            : JSON.stringify(['item', groups.length]),
        alertId: item?.alertId,
        items: []
      };
      groups.push(group);
      if (alertId !== null) byAlert.set(alertId, group);
    }
    group.items.push(item);
  });
  return groups.map(({ items, ...group }) => ({
    ...group,
    sources: uniqueBy(
      items.map((item) => ({ id: item.sourceId, name: item.sourceName })),
      (source) => JSON.stringify([source.id ?? null, source.name ?? null])
    ),
    typologies: uniqueBy(
      items
        .filter(
          (item) => hasText(item.typologyId) || hasText(item.typologyName)
        )
        .map((item) => ({ id: item.typologyId, name: item.typologyName })),
      (typology) =>
        JSON.stringify(
          hasText(typology.id)
            ? ['id', String(typology.id)]
            : ['name', typology.name]
        )
    ),
    rules: uniqueBy(
      items
        .filter((item) => hasText(item.rule))
        .map((item) => String(item.rule))
    ),
    partyKeys: uniqueBy(
      items
        .filter((item) => hasText(item.partyKey))
        .map((item) => String(item.partyKey))
    )
  }));
};

const ruleCell = (group) => {
  if (group.rules.length) return group.rules.join(', ');
  if (group.sources.some((source) => isTmSource(source.id)))
    return (
      <Tooltip title={TM_NO_RULE_TOOLTIP}>
        <span aria-label={TM_NO_RULE_TOOLTIP}>—</span>
      </Tooltip>
    );
  return '-';
};

// FIN-13679 grid (bordered, client-paginated) with FIN-13680 readable cells: names with id fallback,
// an explained dash for AML-TM rules, and "name (key)" for the matched party. Rows are FIN-13678
// alert groups (see groupEvidenceByAlert).
const evidenceColumns = (row) => [
  { key: 'alertId', dataIndex: 'alertId', title: 'Alert ID', render: display },
  {
    key: 'source',
    title: 'Source',
    render: (_, group) =>
      joined(
        group.sources
          .map((source) => source.name ?? source.id)
          .filter(hasText)
          .map(String)
      )
  },
  {
    key: 'typology',
    title: 'Typology',
    render: (_, group) =>
      joined(
        group.typologies.map((typology) => String(typology.name ?? typology.id))
      )
  },
  {
    key: 'rule',
    title: 'Rule',
    render: (_, group) => ruleCell(group)
  },
  {
    key: 'partyKey',
    title: 'Matched party',
    render: (_, group) =>
      joined(
        group.partyKeys.map((partyKey) => matchedPartyLabel(partyKey, row))
      )
  }
];

// FIN-13679: an alerted transaction can carry dozens of associations; use the standard
// bordered, client-paginated grid instead of a raw <table> that grew unbounded.
// FIN-13678: the grid pages over alerts, not over (alert, typology) associations.
export const FramlTransactionEvidence = ({ evidence, row }) => (
  <div role="region" aria-label="Available trigger associations">
    <strong>Available trigger associations</strong>{' '}
    <Tooltip title={TRIGGER_ASSOCIATIONS_SCOPE_TOOLTIP}>
      <InfoCircleOutlined aria-label={TRIGGER_ASSOCIATIONS_SCOPE_TOOLTIP} />
    </Tooltip>
    <Table
      bordered
      size="small"
      columns={evidenceColumns(row)}
      dataSource={groupEvidenceByAlert(evidence)}
      rowKey="groupKey"
      pagination={{
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        hideOnSinglePage: true
      }}
    />
  </div>
);

FramlTransactionEvidence.propTypes = {
  evidence: PropTypes.array.isRequired,
  row: PropTypes.object
};
